// features/ui/uiSlice.ts
import { createAppSlice } from "../../app/createAppSlice";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../../app/store";
import { JSX } from "react/jsx-runtime";
import quanities from "../../quantities.json";
import symbols from "../../symbols.json";
import { calcStepKey } from "../projects/calcProgress";
import type { CalcOperation, CalcPhase, CalcProgressLine } from "../projects/calcProgress";
import { defaultFeatureFlags, FEATURE_FLAG_RC_PROGRESS } from "./featureFlags";

export type ViewportState = "xy" | "xz" | "xy+xz" | "3d";
// -split zooms are only used in split/xy+xz view
export type ZoomState = "all" | "cross" | "side" | "interaction" | "side-split" | "interaction-split";

export type Render3dEngine = "three" | "svg";
export type HiddenLines3d = "hide" | "dashed" | "grey" | "dashedGrey" | "solid";
/** One x offset (m) per result board lane: N, M/V, d. */
export type ResultBoardHeaderOffsets3d = [number, number, number];

export const MOBILE_WARNING_BREAKPOINT = 768;

/**
 * One step of a calculation, as the progress overlay lists them. `detail` is a name out of the
 * project and needs no translation; `labelKey` is how the phase itself is named, so an entry can
 * be rendered even before any engine reported a name.
 */
export interface CalcProgressStep {
  /** Identity of the step — two lines with the same key are the same step. */
  key: string;
  phase: CalcPhase;
  labelKey: string;
  detail?: string;
  /** The load combination this step belongs to, when the engine named one. */
  context?: string;
  /** Set instead of `context` when this step names what the steps after it belong to. */
  contextLabelKey?: string;
  /** Which repetition of the code check this step belongs to; design runs only. */
  designPass?: number;
  /** Which bending direction (plane variant) the step came from — "XZ" or "XY". */
  direction?: string;
  /** Set when the step reads from a label of its own rather than from `detail`. */
  stepLabelKey?: string;
  step?: number;
  totalSteps?: number;
}

interface UIState {
  /** <modalId, isOpen> dictionary - gives you unlimited named modals */
  modals: Record<string, boolean>;
  showOffCanvas: boolean;
  isFullscreen: boolean;
  mobileWarningBreakpoint: number;
  isMobile: boolean;
  disableUser: boolean;
  activeAccordionKeys: string[];
  accordionAllKeys: string[];
  isAccordionCollapsed: boolean;
  alerts: {
    id: string;
    type: string;
    status: string | JSX.Element;
    text: string | JSX.Element;
    context?: string;
    keep?: boolean;
    // Who raised this alert, for alerts that need to be retracted as a group rather than by id.
    // "calc" marks the ones read out of a calculation's log (see showCalcWarnings): they are kept
    // on screen until dismissed, so a new calculation run has to clear the previous run's set or a
    // warning about a column the user has since fixed would stay up.
    // "input" marks the ones derived from the load input rather than from a run (see
    // secondOrderWarnings): those survive calcRunStarted, because the input they describe does,
    // and their raiser retracts them by id as soon as the input stops qualifying.
    // Unset for everything else.
    source?: string;
  }[];
  quantites: Record<string, any>;
  symbols: Record<string, any>;
  viewportState: ViewportState;
  zoomState: ZoomState;

  // this is the state for the the 3 toggles in the toolbar
  // * buckling (beta)
  // * dimensions/meassurement (looks like a cube)
  // * reactions (capital R)
  showSectionBuckling: boolean;
  showSectionDimensions: boolean;
  showSectionReactions: boolean;
  showSectionFireIndicator: boolean;
  loadingMessage: string;

  // ---- 3D viewport settings ----
  // Not persisted across reloads (same as the other showSection* display toggles above).
  render3dEngine: Render3dEngine;
  rightHand3d: boolean;
  scale3dX: number;
  scale3dYZ: number;
  cardboardDistance3d: number;
  resultBoardLaneWidth3d: number;
  resultBoardGraphAmplitude3d: number;
  resultBoardClearance3d: number;
  /** Offset along the column x axis, in metres, of each lane's header anchor (N, M/V, d) on the
   * result board on the left respectively the right of the screen; positive moves it further
   * from the board's top. */
  resultBoardHeaderOffsetsLeft3d: ResultBoardHeaderOffsets3d;
  resultBoardHeaderOffsetsRight3d: ResultBoardHeaderOffsets3d;
  offsetLoadsFromColumn3d: boolean;
  loadColumnSpacing3d: number;
  axisSize3d: number;
  axisOffset3d: number;
  axisLabelOffset3d: number;
  startCardboardsAtOuterCenterLine3d: boolean;
  show3dLoadsOnOppositeSide: boolean;
  show3dCardboard: boolean;
  show3dReinforcement: boolean;
  /** When true, the 3D views draw only the column outline/geometry (and reinforcement) - loads,
   * result boards/diagrams, buckling curve, reactions, and supports are all skipped. Useful for
   * inspecting the extruded cross-section shape in isolation. */
  geometryDebug3d: boolean;
  /** When true, each kept vertical extrusion edge (see extrusionEdges.ts) is annotated with a
   * debug title/label explaining why it was kept (silhouette/crease dot products and angle). */
  debugExtrusion3d: boolean;
  /** Draws each extruded side face's calculated normal, colored by its facing classification and
   * labelled with its face indices and view-direction dot product. */
  debugExtrusionNormals3d: boolean;
  /** Center of the displayed extrusion-normal range along the ordered outline, in percent. */
  debugExtrusionNormalsCenter3d: number;
  /** Total width of the displayed extrusion-normal range, in percent. */
  debugExtrusionNormalsRadius3d: number;
  /** When true, the extruded column is filled with its material color instead of being left as an
   * outline-only wireframe. */
  drawFaces3d: boolean;
  /** When true (and drawFaces3d is also true), faces use normal-dot lighting instead of material colors. */
  shadeFaces3d: boolean;
  /** Shade-faces light direction: degrees (0-360) about the column axis from +y towards +z. */
  lightYaw3d: number;
  /** Shade-faces light direction: degrees (0-360) up from the cross-section plane. */
  lightPitch3d: number;
  /** Draws the shade-faces light direction in the axis widget. */
  showLightDirection3d: boolean;
  /** Shaded timber column: how far the lit end moves the material color towards white, in percent. */
  shadeLighterTimberPercent3d: number;
  /** Shaded non-timber column: how far the lit end moves the material color towards white, in percent. */
  shadeLighterPercent3d: number;
  /** Shaded column: how far the unlit end and bottom move the material color towards black, in percent. */
  shadeDarkerPercent3d: number;
  /** How outline edges whose adjacent faces all face away from the viewer are drawn: left out,
   * dashed, grey, dashed and grey, or like any other outline. */
  hiddenLines3d: HiddenLines3d;
  /** Grey of the grey hidden-line modes, in percent from white (0) to black (100). */
  hiddenLineGrey3d: number;

  // ---- Long calculation progress (/analyse, /codecheck, /design) ----
  // Deliberately kept in UISlice and NOT in ProjectSlice: syncMiddleware treats every
  // "ProjectSlice/"-prefixed action as an input change, dispatching pruneOutput() (which wipes
  // calculation output) and scheduling a debounced PUT of the whole project. A progress tick
  // there would delete the very results being calculated. uiSlice actions are ignored by it.
  /** Which calculation is running, set the moment the request is dispatched. */
  calcOperation: CalcOperation | null;
  /** Local clock for the elapsed-time display; independent of when progress lines arrive. */
  calcStartedAt: number | null;
  /** Latest progress line from the server. Null until the first one arrives (~800 ms in). */
  calcProgress: CalcProgressLine | null;
  /**
   * The steps this run has been through, oldest first, the last one being the step in progress.
   * A step is one distinct thing the server reported working on, so consecutive lines about the
   * same thing (a counter ticking upwards) update the last entry instead of appending to it.
   */
  calcProgressSteps: CalcProgressStep[];

  /**
   * Runtime feature flags, by key, as declared in ./featureFlags. Held here rather than persisted
   * anywhere, so they are off again after a refresh — see that file for why.
   */
  featureFlags: Record<string, boolean>;

  /**
   * The last analysis produced section forces, but the axial force reaches the critical (Euler)
   * load, so the 2nd-order results are meaningless (see the critical_load_exceeded_ alert in
   * App.tsx). Code check and design are gated on it exactly like they are on a missing analysis:
   * there is nothing worth checking until the input changes. Derived from the analysis output,
   * kept here rather than in ProjectSlice so it neither prunes the output nor triggers a save.
   */
  criticalLoadExceeded: boolean;

  /**
   * Guids of the hand-placed bars that failed the exact-bar check when a calculation was last
   * refused over them (see exactBarChecks). Only shrinks between presses — useExactBarMarks drops a
   * bar as soon as it passes. Kept here rather than in ProjectSlice for the same reason as the
   * progress fields: a ProjectSlice action would prune the output and schedule a save.
   */
  flaggedExactBars: string[];
}

const initialState: UIState = {
  modals: {},
  showOffCanvas: false,
  isFullscreen: false,
  mobileWarningBreakpoint: MOBILE_WARNING_BREAKPOINT,
  isMobile: false,
  activeAccordionKeys: ["1", "2", "5", "6"] as string[],
  accordionAllKeys: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11"] as string[], // Central list of ALL keys
  isAccordionCollapsed: false,
  alerts: [],
  quantites: quanities,
  symbols: symbols,
  viewportState: "xy+xz",
  zoomState: "all",
  showSectionBuckling: false,
  showSectionDimensions: true,
  showSectionReactions: true,
  showSectionFireIndicator: false,
  disableUser: false,
  loadingMessage: "Loading...",
  render3dEngine: "svg",
  rightHand3d: true,
  scale3dX: 1,
  scale3dYZ: 1,
  cardboardDistance3d: 0.2,
  resultBoardLaneWidth3d: 0.8,
  resultBoardGraphAmplitude3d: 0.3,
  resultBoardClearance3d: 0,
  resultBoardHeaderOffsetsLeft3d: [0, 0, 0],
  resultBoardHeaderOffsetsRight3d: [0, 0, 0],
  offsetLoadsFromColumn3d: true,
  loadColumnSpacing3d: 0.15,
  axisSize3d: 0.2,
  axisOffset3d: 0.7,
  axisLabelOffset3d: 10,
  startCardboardsAtOuterCenterLine3d: true,
  show3dLoadsOnOppositeSide: true,
  show3dCardboard: false,
  show3dReinforcement: false,
  geometryDebug3d: false,
  debugExtrusion3d: false,
  debugExtrusionNormals3d: false,
  debugExtrusionNormalsCenter3d: 50,
  debugExtrusionNormalsRadius3d: 50,
  drawFaces3d: true,
  shadeFaces3d: true,
  lightYaw3d: 60,
  lightPitch3d: 30,
  showLightDirection3d: false,
  shadeLighterTimberPercent3d: 20,
  shadeLighterPercent3d: 100,
  shadeDarkerPercent3d: 20,
  hiddenLines3d: "grey",
  hiddenLineGrey3d: 25,
  calcOperation: null,
  calcStartedAt: null,
  calcProgress: null,
  calcProgressSteps: [],
  featureFlags: defaultFeatureFlags(),
  criticalLoadExceeded: false,
  flaggedExactBars: [],
};

/** How many steps of one run the overlay keeps. Only the tail is ever read. */
const MAX_PROGRESS_STEPS = 200;

export const UISlice = createAppSlice({
  name: "uiSlice",
  initialState,
  reducers: {
    /** Open a modal by its ID */
    openModal: (state, action: PayloadAction<string>) => {
      state.modals[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<string>) => {
      state.modals[action.payload] = false;
    },
    toggleModal: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.modals[id] = !state.modals[id];
    },
    toggleDisableUser: (state, action: PayloadAction<boolean>) => {
      state.disableUser = action.payload;
    },
    // --- OffCanvas Actions ---
    openOffCanvas: (s) => {
      s.showOffCanvas = true;
    },
    closeOffCanvas: (s) => {
      s.showOffCanvas = false;
    },
    toggleOffCanvas: (s) => {
      s.showOffCanvas = !s.showOffCanvas;
    },
    // --- Fullscreen Actions ---
    enableFullscreen: (s) => {
      s.isFullscreen = true;
    },
    disableFullscreen: (s) => {
      s.isFullscreen = false;
    },

    toggleFullscreen: (s) => {
      s.isFullscreen = !s.isFullscreen;
    },
    // --- Accordion Actions ---
    toggleAccordionKey: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      if (state.activeAccordionKeys.includes(key)) {
        state.activeAccordionKeys = state.activeAccordionKeys.filter((k) => k !== key);
      } else {
        state.activeAccordionKeys.push(key);
      }
    },
    // --- Mobile screen size check ---
    setBreakpoint(state, action: PayloadAction<number>) {
      state.mobileWarningBreakpoint = action.payload;
      state.isMobile = window.innerWidth < action.payload;
    },
    setLoadingMessage(state, action: PayloadAction<string>) {
      state.loadingMessage = action.payload;
    },
    // ---- Long calculation progress ----
    /** A calculation request has been dispatched; show the overlay and start the local clock. */
    calcRunStarted(state, action: PayloadAction<CalcOperation>) {
      state.calcOperation = action.payload;
      state.calcStartedAt = Date.now();
      state.calcProgress = null;
      state.calcProgressSteps = [];
      // The warnings read out of the previous run's log describe the previous input. They are
      // sticky, so they have to be retracted here rather than left to expire.
      state.alerts = state.alerts.filter((a) => a.source !== "calc");
    },
    setCalcProgress(state, action: PayloadAction<CalcProgressLine>) {
      const line = action.payload;
      state.calcProgress = line;

      // The step list is part of the #261 feature, and the server still sends its ordinary
      // interpolated phase lines with the flag off — so without this guard every calculation grew
      // a step list it never had before. The flag lives in this slice, so there is nothing to
      // thread through.
      if (!state.featureFlags[FEATURE_FLAG_RC_PROGRESS]) {
        return;
      }

      // What makes one step distinct from the next: the phase, what is being worked on, and what
      // it belongs to. Not the counter — "3 of 24" and "4 of 24" of the same named thing are one
      // step whose count moved on, and listing each would bury the steps that matter.
      //
      // The context has to be part of this: without it "Shear capacity" under LC1 and the same
      // step under LC2 are one key, and every combination after the first would silently
      // overwrite the previous one instead of appending.
      // Computed by the server and shared with the dispatch throttle in projectSlice — see
      // calcStepKey. Deriving it here was how the design pass, the direction and the load
      // combination's ordinal each came to be missing from it in turn.
      const key = calcStepKey(line);
      const last = state.calcProgressSteps[state.calcProgressSteps.length - 1];
      const entry: CalcProgressStep = {
        key,
        phase: line.phase,
        labelKey: line.labelKey,
        detail: line.detail,
        context: line.context,
        contextLabelKey: line.contextLabelKey,
        designPass: line.designPass,
        direction: line.direction,
        stepLabelKey: line.stepLabelKey,
        step: line.step,
        totalSteps: line.totalSteps,
      };

      if (last?.key === key) {
        state.calcProgressSteps[state.calcProgressSteps.length - 1] = entry;
        return;
      }

      state.calcProgressSteps.push(entry);
      // A long run reports thousands of steps and nobody scrolls back that far; keeping the tail
      // bounded keeps the list (and this state) from growing without limit.
      if (state.calcProgressSteps.length > MAX_PROGRESS_STEPS) {
        state.calcProgressSteps.splice(0, state.calcProgressSteps.length - MAX_PROGRESS_STEPS);
      }
    },
    /** The calculation settled — succeeded, failed or was cancelled. */
    calcRunFinished(state) {
      state.calcOperation = null;
      state.calcStartedAt = null;
      state.calcProgress = null;
      state.calcProgressSteps = [];
    },
    setIsMobile(state, action: PayloadAction<boolean>) {
      state.isMobile = action.payload;
    },
    setAccordionKeys: (state, action: PayloadAction<string[]>) => {
      state.activeAccordionKeys = action.payload;
    },
    toggleCollapseAllAccordion: (state) => {
      if (state.isAccordionCollapsed) {
        // Expand all
        state.activeAccordionKeys = [...state.accordionAllKeys];
        state.isAccordionCollapsed = false;
      } else {
        // Collapse all
        state.activeAccordionKeys = [];
        state.isAccordionCollapsed = true;
      }
    },
    toggleShowSectionBuckling: (state) => {
      state.showSectionBuckling = !state.showSectionBuckling;
    },
    setShowSectionBuckling: (state, action: PayloadAction<boolean>) => {
      state.showSectionBuckling = action.payload;
    },
    toggleShowSectionDimensions: (state) => {
      state.showSectionDimensions = !state.showSectionDimensions;
    },
    toggleShowSectionReactions: (state) => {
      state.showSectionReactions = !state.showSectionReactions;
    },
    toggleShowSectionFireIndicator: (state) => {
      state.showSectionFireIndicator = !state.showSectionFireIndicator;
    },
    setShowSectionFireIndicator: (state, action: PayloadAction<boolean>) => {
      state.showSectionFireIndicator = action.payload;
    },
    // ---- Alerts ----
    addAlert: (state, action: PayloadAction<UIState["alerts"][number]>) => {
      const incoming = action.payload;

      // Duplicate prevention
      const exists = state.alerts.some((a) => a.text?.toString().trim() === incoming.text?.toString().trim());

      if (!exists) {
        state.alerts.push(incoming);
      }
    },
    removeAlert: (state, action: PayloadAction<string>) => {
      state.alerts = state.alerts.filter((a) => a.id !== action.payload);
    },
    setViewportState: (state, action: PayloadAction<ViewportState>) => {
      const oldState = state.viewportState;
      state.viewportState = action.payload;
      const newState = state.viewportState;

      // the 3d view is a single panel with no cross/side/interaction sub-panels of its own, so
      // any zoom state other than "all" (e.g. left over from zooming into "cross"/"interaction"
      // in another mode) would hide the 3d panel entirely with no way to recover it - reset to
      // "all" whenever switching into 3d mode.
      if (newState === "3d") {
        state.zoomState = "all";
      }

      // if the "new" viewport state is not a split, then change zoom to non-split
      if (newState !== "xy+xz") {
        switch (state.zoomState) {
          case "interaction-split":
            state.zoomState = "interaction";
            break;
          case "side-split":
            state.zoomState = "side";
            break;
        }
      }

      // going from non split to split
      if (oldState === "xz" && newState == "xy+xz") {
        // this is only relevant for xz since interaction/side is xy
        switch (state.zoomState) {
          case "interaction":
            state.zoomState = "interaction-split";
            break;
          case "side":
            state.zoomState = "side-split";
            break;
        }
      }
    },
    toggleZoomState: (state, action: PayloadAction<ZoomState>) => {
      const request = action.payload;
      const old = state.zoomState;
      state.zoomState = old === "all" ? request : "all";
    },
    setRender3dEngine: (state, action: PayloadAction<Render3dEngine>) => {
      state.render3dEngine = action.payload;
    },
    setRightHand3d: (state, action: PayloadAction<boolean>) => {
      state.rightHand3d = action.payload;
    },
    setScale3dX: (state, action: PayloadAction<number>) => {
      state.scale3dX = action.payload;
    },
    setScale3dYZ: (state, action: PayloadAction<number>) => {
      state.scale3dYZ = action.payload;
    },
    setCardboardDistance3d: (state, action: PayloadAction<number>) => {
      state.cardboardDistance3d = action.payload;
    },
    setResultBoardLaneWidth3d: (state, action: PayloadAction<number>) => {
      state.resultBoardLaneWidth3d = action.payload;
    },
    setResultBoardGraphAmplitude3d: (state, action: PayloadAction<number>) => {
      state.resultBoardGraphAmplitude3d = action.payload;
    },
    setResultBoardClearance3d: (state, action: PayloadAction<number>) => {
      state.resultBoardClearance3d = action.payload;
    },
    setResultBoardHeaderOffset3d: (
      state,
      action: PayloadAction<{ side: "left" | "right"; lane: number; value: number }>,
    ) => {
      const { side, lane, value } = action.payload;
      const offsets = side === "left" ? state.resultBoardHeaderOffsetsLeft3d : state.resultBoardHeaderOffsetsRight3d;
      if (lane >= 0 && lane < offsets.length) offsets[lane] = value;
    },
    setOffsetLoadsFromColumn3d: (state, action: PayloadAction<boolean>) => {
      state.offsetLoadsFromColumn3d = action.payload;
    },
    setLoadColumnSpacing3d: (state, action: PayloadAction<number>) => {
      state.loadColumnSpacing3d = action.payload;
    },
    setAxisSize3d: (state, action: PayloadAction<number>) => {
      state.axisSize3d = action.payload;
    },
    setAxisOffset3d: (state, action: PayloadAction<number>) => {
      state.axisOffset3d = action.payload;
    },
    setAxisLabelOffset3d: (state, action: PayloadAction<number>) => {
      state.axisLabelOffset3d = action.payload;
    },
    setStartCardboardsAtOuterCenterLine3d: (state, action: PayloadAction<boolean>) => {
      state.startCardboardsAtOuterCenterLine3d = action.payload;
    },
    setShow3dLoadsOnOppositeSide: (state, action: PayloadAction<boolean>) => {
      state.show3dLoadsOnOppositeSide = action.payload;
    },
    setShow3dCardboard: (state, action: PayloadAction<boolean>) => {
      state.show3dCardboard = action.payload;
    },
    setShow3dReinforcement: (state, action: PayloadAction<boolean>) => {
      state.show3dReinforcement = action.payload;
    },
    setGeometryDebug3d: (state, action: PayloadAction<boolean>) => {
      state.geometryDebug3d = action.payload;
    },
    setDebugExtrusion3d: (state, action: PayloadAction<boolean>) => {
      state.debugExtrusion3d = action.payload;
    },
    setDebugExtrusionNormals3d: (state, action: PayloadAction<boolean>) => {
      state.debugExtrusionNormals3d = action.payload;
    },
    setDebugExtrusionNormalsCenter3d: (state, action: PayloadAction<number>) => {
      state.debugExtrusionNormalsCenter3d = action.payload;
    },
    setDebugExtrusionNormalsRadius3d: (state, action: PayloadAction<number>) => {
      state.debugExtrusionNormalsRadius3d = action.payload;
    },
    setDrawFaces3d: (state, action: PayloadAction<boolean>) => {
      state.drawFaces3d = action.payload;
    },
    setShadeFaces3d: (state, action: PayloadAction<boolean>) => {
      state.shadeFaces3d = action.payload;
    },
    setLightYaw3d: (state, action: PayloadAction<number>) => {
      state.lightYaw3d = action.payload;
    },
    setLightPitch3d: (state, action: PayloadAction<number>) => {
      state.lightPitch3d = action.payload;
    },
    setShowLightDirection3d: (state, action: PayloadAction<boolean>) => {
      state.showLightDirection3d = action.payload;
    },
    setShadeLighterTimberPercent3d: (state, action: PayloadAction<number>) => {
      state.shadeLighterTimberPercent3d = action.payload;
    },
    setShadeLighterPercent3d: (state, action: PayloadAction<number>) => {
      state.shadeLighterPercent3d = action.payload;
    },
    setShadeDarkerPercent3d: (state, action: PayloadAction<number>) => {
      state.shadeDarkerPercent3d = action.payload;
    },
    setHiddenLines3d: (state, action: PayloadAction<HiddenLines3d>) => {
      state.hiddenLines3d = action.payload;
    },
    setHiddenLineGrey3d: (state, action: PayloadAction<number>) => {
      state.hiddenLineGrey3d = action.payload;
    },
    setCriticalLoadExceeded: (state, action: PayloadAction<boolean>) => {
      state.criticalLoadExceeded = action.payload;
    },
    setFlaggedExactBars: (state, action: PayloadAction<string[]>) => {
      state.flaggedExactBars = action.payload;
    },
    setFeatureFlag: (state, action: PayloadAction<{ key: string; enabled: boolean }>) => {
      state.featureFlags[action.payload.key] = action.payload.enabled;
    },
  },
  selectors: {
    /** Entire modal map (useful for debugging) */
    selectModals: (state: UIState) => state.modals,
    selectShowOffCanvas: (state) => state.showOffCanvas,
    selectFullscreen: (state) => state.isFullscreen,
    selectBreakPoint: (state) => state.mobileWarningBreakpoint,
    selectIsMobile: (state) => state.isMobile,
    selectActiveAccordionKeys: (state) => state.activeAccordionKeys,
    selectIsAccordionCollapsed: (state) => state.isAccordionCollapsed,
    selectAlerts: (state: UIState) => state.alerts,
    selectQuantity: (state: UIState) => state.quantites,
    selectSymbols: (state: UIState) => state.symbols,
    selectViewportState: (state: UIState) => state.viewportState,
    selectZoomState: (state: UIState) => state.zoomState,

    selectShowSectionBuckling: (state: UIState) => state.showSectionBuckling,
    selectShowSectionDimensions: (state: UIState) => state.showSectionDimensions,
    selectShowSectionReactions: (state: UIState) => state.showSectionReactions,
    selectShowSectionFireIndicator: (state: UIState) => state.showSectionFireIndicator,
    selectDisableUser: (state: UIState) => state.disableUser,
    selectLoadingMessage: (state: UIState) => state.loadingMessage,
    selectCalcOperation: (state: UIState) => state.calcOperation,
    selectCalcStartedAt: (state: UIState) => state.calcStartedAt,
    selectCalcProgress: (state: UIState) => state.calcProgress,
    selectCalcProgressSteps: (state: UIState) => state.calcProgressSteps,
    selectRender3dEngine: (state: UIState) => state.render3dEngine,
    selectRightHand3d: (state: UIState) => state.rightHand3d,
    selectScale3dX: (state: UIState) => state.scale3dX,
    selectScale3dYZ: (state: UIState) => state.scale3dYZ,
    selectCardboardDistance3d: (state: UIState) => state.cardboardDistance3d,
    selectResultBoardLaneWidth3d: (state: UIState) => state.resultBoardLaneWidth3d,
    selectResultBoardGraphAmplitude3d: (state: UIState) => state.resultBoardGraphAmplitude3d,
    selectResultBoardClearance3d: (state: UIState) => state.resultBoardClearance3d,
    selectResultBoardHeaderOffsetsLeft3d: (state: UIState) => state.resultBoardHeaderOffsetsLeft3d,
    selectResultBoardHeaderOffsetsRight3d: (state: UIState) => state.resultBoardHeaderOffsetsRight3d,
    selectOffsetLoadsFromColumn3d: (state: UIState) => state.offsetLoadsFromColumn3d,
    selectLoadColumnSpacing3d: (state: UIState) => state.loadColumnSpacing3d,
    selectAxisSize3d: (state: UIState) => state.axisSize3d,
    selectAxisOffset3d: (state: UIState) => state.axisOffset3d,
    selectAxisLabelOffset3d: (state: UIState) => state.axisLabelOffset3d,
    selectStartCardboardsAtOuterCenterLine3d: (state: UIState) => state.startCardboardsAtOuterCenterLine3d,
    selectShow3dLoadsOnOppositeSide: (state: UIState) => state.show3dLoadsOnOppositeSide,
    selectShow3dCardboard: (state: UIState) => state.show3dCardboard,
    selectShow3dReinforcement: (state: UIState) => state.show3dReinforcement,
    selectGeometryDebug3d: (state: UIState) => state.geometryDebug3d,
    selectDebugExtrusion3d: (state: UIState) => state.debugExtrusion3d,
    selectDebugExtrusionNormals3d: (state: UIState) => state.debugExtrusionNormals3d,
    selectDebugExtrusionNormalsCenter3d: (state: UIState) => state.debugExtrusionNormalsCenter3d,
    selectDebugExtrusionNormalsRadius3d: (state: UIState) => state.debugExtrusionNormalsRadius3d,
    selectDrawFaces3d: (state: UIState) => state.drawFaces3d,
    selectShadeFaces3d: (state: UIState) => state.shadeFaces3d,
    selectLightYaw3d: (state: UIState) => state.lightYaw3d,
    selectLightPitch3d: (state: UIState) => state.lightPitch3d,
    selectShowLightDirection3d: (state: UIState) => state.showLightDirection3d,
    selectShadeLighterTimberPercent3d: (state: UIState) => state.shadeLighterTimberPercent3d,
    selectShadeLighterPercent3d: (state: UIState) => state.shadeLighterPercent3d,
    selectShadeDarkerPercent3d: (state: UIState) => state.shadeDarkerPercent3d,
    selectHiddenLines3d: (state: UIState) => state.hiddenLines3d,
    selectHiddenLineGrey3d: (state: UIState) => state.hiddenLineGrey3d,
    selectCriticalLoadExceeded: (state: UIState) => state.criticalLoadExceeded,
    selectFlaggedExactBars: (state: UIState) => state.flaggedExactBars,
    selectFeatureFlags: (state: UIState) => state.featureFlags,
  },
});

// Export the actions and selectors so they can be used in components
export const {
  openModal,
  closeModal,
  toggleModal,
  openOffCanvas,
  closeOffCanvas,
  toggleOffCanvas,
  enableFullscreen,
  disableFullscreen,
  toggleFullscreen,
  setBreakpoint,
  setIsMobile,
  toggleAccordionKey,
  setAccordionKeys,
  toggleCollapseAllAccordion,
  addAlert,
  removeAlert,
  setViewportState,
  toggleZoomState,
  toggleShowSectionBuckling,
  setShowSectionBuckling,
  toggleShowSectionDimensions,
  toggleShowSectionReactions,
  toggleShowSectionFireIndicator,
  setShowSectionFireIndicator,
  setLoadingMessage,
  calcRunStarted,
  setCalcProgress,
  calcRunFinished,
  setRender3dEngine,
  setRightHand3d,
  setScale3dX,
  setScale3dYZ,
  setCardboardDistance3d,
  setResultBoardLaneWidth3d,
  setResultBoardGraphAmplitude3d,
  setResultBoardClearance3d,
  setResultBoardHeaderOffset3d,
  setOffsetLoadsFromColumn3d,
  setLoadColumnSpacing3d,
  setAxisSize3d,
  setAxisOffset3d,
  setAxisLabelOffset3d,
  setStartCardboardsAtOuterCenterLine3d,
  setShow3dLoadsOnOppositeSide,
  setShow3dCardboard,
  setShow3dReinforcement,
  setGeometryDebug3d,
  setDebugExtrusion3d,
  setDebugExtrusionNormals3d,
  setDebugExtrusionNormalsCenter3d,
  setDebugExtrusionNormalsRadius3d,
  setDrawFaces3d,
  setShadeFaces3d,
  setLightYaw3d,
  setLightPitch3d,
  setShowLightDirection3d,
  setShadeLighterTimberPercent3d,
  setShadeLighterPercent3d,
  setShadeDarkerPercent3d,
  setHiddenLines3d,
  setHiddenLineGrey3d,
  setCriticalLoadExceeded,
  setFlaggedExactBars,
  setFeatureFlag,
} = UISlice.actions;

export const {
  selectModals,
  selectShowOffCanvas,
  selectFullscreen,
  selectBreakPoint,
  selectIsMobile,
  selectActiveAccordionKeys,
  selectIsAccordionCollapsed,
  selectAlerts,
  selectViewportState,
  selectZoomState,
  selectShowSectionBuckling,
  selectShowSectionDimensions,
  selectShowSectionReactions,
  selectShowSectionFireIndicator,
  selectDisableUser,
  selectLoadingMessage,
  selectCalcOperation,
  selectCalcStartedAt,
  selectCalcProgress,
  selectCalcProgressSteps,
  selectRender3dEngine,
  selectRightHand3d,
  selectScale3dX,
  selectScale3dYZ,
  selectCardboardDistance3d,
  selectResultBoardLaneWidth3d,
  selectResultBoardGraphAmplitude3d,
  selectResultBoardClearance3d,
  selectResultBoardHeaderOffsetsLeft3d,
  selectResultBoardHeaderOffsetsRight3d,
  selectOffsetLoadsFromColumn3d,
  selectLoadColumnSpacing3d,
  selectAxisSize3d,
  selectAxisOffset3d,
  selectAxisLabelOffset3d,
  selectStartCardboardsAtOuterCenterLine3d,
  selectShow3dLoadsOnOppositeSide,
  selectShow3dCardboard,
  selectShow3dReinforcement,
  selectGeometryDebug3d,
  selectDebugExtrusion3d,
  selectDebugExtrusionNormals3d,
  selectDebugExtrusionNormalsCenter3d,
  selectDebugExtrusionNormalsRadius3d,
  selectDrawFaces3d,
  selectShadeFaces3d,
  selectLightYaw3d,
  selectLightPitch3d,
  selectShowLightDirection3d,
  selectShadeLighterTimberPercent3d,
  selectShadeLighterPercent3d,
  selectShadeDarkerPercent3d,
  selectHiddenLines3d,
  selectHiddenLineGrey3d,
  selectCriticalLoadExceeded,
  selectFlaggedExactBars,
  selectFeatureFlags,
} = UISlice.selectors;

/* ------------------------------------------------------------------ */
/* ▸ Parameterised selector (needs its own export)                    */
/*    You call it like: useSelector(selectModalById("appModal"))      */
/* ------------------------------------------------------------------ */
export const selectModalById = (id: string) => (state: RootState) => !!state.uiSlice.modals[id]; // ⬅️ adapt key if you mounted slice under a different name
